import { ISFInfomeeting } from "../interfaces";
import {
  infomeetingTypes,
  mergeMarketData,
} from "../helpers/infomeeting-service-helpers";
import { isGatsbyProduction } from "~components/utils/env-helpers";

const SF_BASE_URL = isGatsbyProduction()
  ? "https://hsey-api.azurewebsites.net"
  : "https://hsey-api-dev.azurewebsites.net";
const HSEY_DASH_CLIENT_ID = "d10e9eb5-4670-41e9-8860-7c498b603676";
const HSEY_DASH_CLIENT_SECRET = "8b09ead6-44d5-4932-81a5-643322cc0ad6";

interface IAuthSFToken {
  access_token: string;
  token_type: string;
  expires_in: number;
}

/**
 * Gets bearer token for the SF API
 * Fetches it and stores it in the browser's session storage
 */
const getSFAuthToken = () => {
  const sfAuthTokenKey = `${
    isGatsbyProduction() ? `prod` : `dev`
  }_sf_auth_token`;
  const sfAuthToken = sessionStorage.getItem(sfAuthTokenKey);

  if (sfAuthToken) {
    return Promise.resolve(sfAuthToken);
  }

  const params: any = {
    client_id: HSEY_DASH_CLIENT_ID,
    client_secret: HSEY_DASH_CLIENT_SECRET,
    grant_type: "client_credentials",
  };

  const esc = encodeURIComponent;
  const query = Object.keys(params)
    .map((k) => esc(k) + "=" + esc(params[k]))
    .join("&");

  return fetch(`${SF_BASE_URL}/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: query,
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error(response.statusText);
      }
      return response.json() as Promise<IAuthSFToken>;
    })
    .then((authToken) => {
      sessionStorage.setItem(sfAuthTokenKey, authToken.access_token);
      return authToken.access_token;
    });
};

/**
 * Fetches the infomeeting data for a specific SalesForce market
 */
const getSFMarket = (market: string) => {
  return getSFAuthToken().then((authToken) => {
    return fetch(
      `${SF_BASE_URL}/api/infomeetings/getAllInfomeetings?mc=${market}`,
      {
        method: "GET",
        headers: { Authorization: `bearer ${authToken}` },
      }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.statusText);
        }
        return response.json() as Promise<ISFInfomeeting>;
      })
      .then((sfInfomeeting) => {
        return sfInfomeeting.data;
      });
  });
};

/**
 * Fetches all infomeeting data for a specific market
 */
// TODO: if need to test on localhost as currently
// the dev endpoint seems to be broken, use test json data
// supplied in below code.
const testData: any = require("./devdump.json");
const getAllInfomeetingsForMarket = (
  market: string,
  meetingType: "infomeetings" | "events" = "infomeetings"
) => {
  try {
    // remove false for test in localhost.
    if (!isGatsbyProduction() && false) {
      const filteredData = testData.data.filter((meeting: any) =>
        infomeetingTypes[meetingType].includes(meeting.meeting_Type__c)
      );
      return mergeMarketData(market, filteredData);
    } else {
      return getSFMarket(market).then((data) => {
        // TODO: assign devdump for testing here
        // swap data for testData.data.filter.... for testing in local.
        const filteredData = data.filter((meeting) =>
          infomeetingTypes[meetingType].includes(meeting.meeting_Type__c)
        );
        return mergeMarketData(market, filteredData);
      });
    }
  } catch (e) {
    return [];
  }
};

export { getAllInfomeetingsForMarket, getSFAuthToken };
